import { CustomElement, OverrideCustomElementConfig } from "@cervval.js/client-html5-core/src/components/customElement.decorator";
import { WebComponent } from "@cervval.js/client-html5-core/src/components/webComponent";
import { FLEX_CONTAINER_COLUMN } from "@cervval.js/client-html5-core/src/tools/cssClasses.tools";
import { createHtmlElement, empty } from "@cervval.js/client-html5-core/src/tools/dom.tools";
import { LOG_SERVICE, LogService } from "@cervval.js/common/services";
import { Injected } from "@cervval.js/common/src/dependencies/injected";
import { toPx } from "@cervval.js/common/src/tools/number.tools";
import { secondsToHours } from "@cervval.js/common/src/tools/time.tools";

/** AgendaTimescale tick css class */
const DAY_AGENDA_TIMESCALE_TICK_CLASS = "day-agenda-timescale-tick";

/** Information on a agendaTimescale step. Used to draw agendaTimescale ticks. */
export interface DayAgendaTimescaleInfo {

    /** Text to be displayed. Usually the date formatted */
    text: string;

    /** Height of tick */
    height: number;

    /** Start time of tick (in seconds) */
    startTime: number;
}

/** AgendaTimescale context containing start and finish timestamp and coordinates */
export interface DayAgendaTimescaleContext {

    /** Uid of tick */
    uid: string;

    /** Tick element start date */
    startTime: number;

    /** Tick element finish date */
    finishTime: number;

    /** Height of timescale */
    height: number;
}


/** Define all available options of agendaTimescale */
export interface DayAgendaTimescaleOptions {
    /** Setting agendaTimescale uid affects the AgendaTimescaleUnitsOptions computation */
    uid?: string;
}

@CustomElement({
    name: "cjs-day-agenda-timescale",
    style: require("./dayAgendaTimescale.scss"),
    elementCssClasses: [FLEX_CONTAINER_COLUMN]
})
export class CjsDayAgendaTimescale extends WebComponent {

    @Injected(LOG_SERVICE, { optional: true })
    protected readonly _log?: LogService;

    /** AgendaTimescale info used to compute ticks */
    private _agendaTimescaleInfos: DayAgendaTimescaleInfo[] = [];

    /**
     * setting agendaTimescale uid affects the AgendaTimescaleUnitsOptions computation
     */
    protected _uid?: string;

    /** AgendaTimescale context */
    private _contexts: DayAgendaTimescaleContext[] = [];

    /** Ticks of timescale */
    protected _ticks: DayAgendaTimescaleInfo[] = [];

    /**
     * The initialization of the smartSelect
     */
    constructor(contexts?: DayAgendaTimescaleContext[], options?: Partial<DayAgendaTimescaleOptions>, customElementOptions?: OverrideCustomElementConfig) {
        super(customElementOptions);
        this._uid = options?.uid;

        if (contexts) {
            this._contexts = contexts;
        }
    }

    /**
     * Refresh agendaTimescale and its context if specified. Compute new agendaTimescale infos and rebuild ticks
     * @param reason refresh reason
     * @param context AgendaTimescale context
     */
    public override refresh(reason?: string, context?: DayAgendaTimescaleContext[]): void {
        // Reset ticks
        this._ticks = [];

        if (context) {
            this._contexts = context;// use provided context
        }

        if (this._contexts) {
            this._agendaTimescaleInfos = this._computeTimeScaleInfos(this._contexts);
            rebuildTicks(this, this._agendaTimescaleInfos);
        }

    }

    /**
     * Compute agendaTimescale infos used later to build agendaTimescale ticks. 
     * @param context AgendaTimescale context
     */
    private _computeTimeScaleInfos(
        context: DayAgendaTimescaleContext[]
    ): DayAgendaTimescaleInfo[] {
        return context.map(c => {
            return {
                text: `${secondsToHours(c.startTime)} h`,
                startTime: c.startTime,
                height: c.height
            };
        });
    }

    /** Returns agendaTimescale's infos (time ticks) */
    public getAgendaTimescaleInfos(): DayAgendaTimescaleInfo[] {
        return this._agendaTimescaleInfos;
    }
}


/**
 * Rebuild agendaTimescale's ticks from agendaTimescale's infos
 * @param scaleDiv agendaTimescale's div
 */
export function rebuildTicksForAgenda(scaleDiv: HTMLElement, agendaTimescaleInfos: DayAgendaTimescaleInfo[]): void {
    rebuildTicks(scaleDiv, agendaTimescaleInfos, { fromAgenda: true });
}


/**
 * Rebuild agendaTimescale's ticks from agendaTimescale's infos
 * @param scaleDiv agendaTimescale's div
 */
export function rebuildTicks(scaleDiv: HTMLElement, agendaTimescaleInfos: DayAgendaTimescaleInfo[], options?: { fromAgenda?: boolean }): void {

    empty(scaleDiv);

    for (const timescaleInfo of agendaTimescaleInfos ?? []) {
        scaleDiv.append(_buildOneTickOnTimescale(timescaleInfo, options));
    }
}


/**
 * Build a div timescale tick from step info and relative to its container location.
 * @param timescaleInfo Timescale step info to draw tick
 * @returns tick's div
 */
function _buildOneTickOnTimescale(timescaleInfo: DayAgendaTimescaleInfo, options?: { fromAgenda?: boolean }): HTMLDivElement {

    let label = timescaleInfo.startTime !== 0 ? String(timescaleInfo.text) : "";
    if (options?.fromAgenda) { label = ""; }

    const div = createHtmlElement("div", {
        cssClass: [DAY_AGENDA_TIMESCALE_TICK_CLASS, options?.fromAgenda ? "tick-agenda" : "tick-timescale"],
        attributes: {
            time: String(timescaleInfo.text)
        },
        innerHTML: label,
        style: {
            height: toPx(timescaleInfo.height),
            minHeight: toPx(timescaleInfo.height)
        }
    });

    return div;
}

